// Reimbursing Town Meeting Members FAQ Data (Warrant Article 5, Fall 2026
// Special Town Meeting)
// Version 1
// October 13, 2026
//
// Content by the petitioner, Catherine Anderson. Source: "Article 5 FAQ.docx"
// (Drive: Town App/FAQs/WA5 Reimburse 5 TMMs FAQ/). The Overview, the
// disclaimer and the July 1, 2026 letter are transcribed from that document,
// including its punctuation and the quoted passages inside the letter.
//
// This page has NO questions yet, so there is no `faqData` export and no FAQ
// tab. The second tab is the letter instead. When questions arrive, add
// `faqData` and restore the FAQ tab from one of the other FAQ pages —
// question ids become permanent at that point, not before.
//
// As on the Cryptocurrency Kiosk FAQ, `disclaimer` IS the source document's own
// disclaimer: the petitioner wrote it about her own FAQ. DisclaimerFooter
// prepends the sitewide sentence, so it is not repeated here.

export const faqSummary = {
  title: "Reimbursing Town Meeting Members FAQ",
  subtitle: "Overview",
  version: "Version 1",
  date: "October 13, 2026",
  disclaimer: "This FAQ is published and updated by Catherine Anderson and represents a good faith interpretation of the intent, content and possible impact of the draft Resolution being proposed by Catherine Anderson. The FAQ is not part of the proposed warrant article, and does not represent the Town of Brookline.",
  intro: "Warrant Article 5 urges the Select Board to reimburse five Town Meeting Members for their incurred legal expenses, in order to defend themselves against an investigation into their legal and legitimate legislative activities regarding the privatization of trash services proposed by the Town in 2025."
};

// The July 1, 2026 letter, shown on its own tab. `meta` is the message header
// as the source reproduces it; `signatories` are the 161 Town Meeting members
// who signed, in the source's order (by precinct, then surname), each with the
// precinct they represent.
export const letter = {
  tabLabel: "Letter to the Select Board July 2026",
  heading: "Section 1: July 1, 2026 letter to Select Board signed by 161 Town Meeting Members",
  meta: [
    "Wed, Jul 1, 2026 at 11:22 AM",
    "To: dpearlman@brooklinema.gov, Bernard Greene <bgreene@brooklinema.gov>, Michael Rubenstein <mrubenstein@brooklinema.gov>, azimmerman@brooklinema.gov",
    "Cc: abuono@brooklinema.gov, Brookline TMMA <brooklinetmma@groups.io>",
  ],
  salutation: "Dear Brookline Select Board,",
  paragraphs: [
    "We write as elected legislators in the Town of Brookline, in support of five Town Meeting colleagues who were erroneously subject to investigation for protected legislative activities. These members spent approximately $24,000 on legal representation in order to properly defend themselves against accusations and threat of prosecution, and we concur with the April 23, 2026, Advisory Committee Resolution requesting the Town reimburse these expenses (adopted 17-0 with 3 abstentions).",
    "This situation arose in early 2025, when five elected Town Meeting members (Anthony Buono, Alec Lebovitz, Joslin Murphy, Richard Murphy and Donelle O’Neal, Sr.), four of whom were also Advisory Committee members and one a Transportation Board member, raised questions about a contract to privatize trash collection as of July 1, 2025. In January this contract with Casella Inc. had been agreed by the Select Board, but a Town Meeting vote was still required in May. During the spring, these members conducted research and spoke at hearings and presentations opposing the new policy. In May, Town Meeting formally approved the privatization contract after extended discussion.",
    "Then, in mid-June, these five were informed about ethics complaints made to the Town by unknown actors never fully described to them. They requested referral to the State Ethics Commission, but instead an outside attorney was hired at a cost to the Town of over $31,000. Out of concern that any misguided findings could lead to removal from appointed seats and prosecution for felony ethics violations, the five members hired outside counsel and incurred expenses of nearly $24,000. Ultimately, four were completely exonerated, and the fifth cited for an alleged ethics violation related to disclosure that his brother was a DPW worker (even though he proudly introduced him as such at the recorded Town Meeting).",
    "This investigation was unwarranted and a serious overreach. There is no indication of any behavior beyond a legislator’s fundamental right to gather information and speak freely about budgetary and other matters under their purview.",
    "A second example of an unwarranted investigative overstep underscores our concerns. Between May and October 2023, complaints were made against David Lescohier, a Town Meeting member who had, from the floor of May Town Meeting, moved to refer a Warrant Article back to the Select Board. The referral vote was successful. Members of an affected organization then complained to the Town, and an outside attorney was hired to conduct further review. The cost for this investigation is not yet known.",
    "Mr. Lescohier was not informed of the nature of the complaint while insistently being asked to submit to an open-ended interview with outside counsel. Eventually he received a letter dated July 30, 2024, from Lloyd Gellineau, the Chief Diversity Officer and Director of the Office of Diversity, Equity and Inclusion & Community Relations for the Town of Brookline, which stated in part: “the responsibility [of CDEICR] does not include the authority to adjudicate controversies or overturn political acts of Town Meeting…following review, the complaint did not constitute a ‘plausibly valid’ claim of discrimination”.",
    "In this case, since no defendant attorney was hired, there is no request for reimbursement. However, this is another example of protected public speech, by a Town Meeting member in their legislative capacity making a motion to refer an article, which should not have been subject to an investigation.",
    "Finally, we highlight the Massachusetts Constitution, Article 21, which provides that “The freedom of deliberation, speech and debate, in either house of the legislature, is so essential to the rights of the people, that it cannot be the foundation of any accusation or prosecution, action or complaint, in any other court or place whatsoever.”",
    "Therefore, we the undersigned 161 Town Meeting members concur with the Advisory Committee Resolution and urge the Select Board to approve timely reimbursement of $24,000 in legal fees.",
  ],
  closing: "Sincerely,",
  signatoryCount: 161,
  signatories: [
    { name: "Catherine Anderson", precinct: 6 },
    { name: "Cathleen Cavell", precinct: 1 },
    { name: "Neil Gordon", precinct: 1 },
    { name: "Sarah Moghtader", precinct: 1 },
    { name: "Bettina Neuefeind", precinct: 1 },
    { name: "Elise Couture-Stone", precinct: 2 },
    { name: "Megan Hinman", precinct: 2 },
    { name: "Brenda Hochberg", precinct: 2 },
    { name: "Colleen Newsome", precinct: 2 },
    { name: "Markus Penzel", precinct: 2 },
    { name: "Kimberley Richardson", precinct: 2 },
    { name: "Pamela Roberts", precinct: 2 },
    { name: "Katha Seidman", precinct: 2 },
    { name: "Maura Toomey", precinct: 2 },
    { name: "David Trevvett", precinct: 2 },
    { name: "Kathryn Becker", precinct: 3 },
    { name: "Mary Dewart", precinct: 3 },
    { name: "Murray Dewart", precinct: 3 },
    { name: "Peter Frumkin", precinct: 3 },
    { name: "Gina Hahn", precinct: 3 },
    { name: "Leigh Heyman", precinct: 3 },
    { name: "Sadaf Kazmi", precinct: 3 },
    { name: "Margaret Robotham", precinct: 3 },
    { name: "Rebecca Stone", precinct: 3 },
    { name: "Anne Turner", precinct: 3 },
    { name: "Gregory Wheeler", precinct: 3 },
    { name: "Arslan Aziz", precinct: 4 },
    { name: "Elizabeth Childs", precinct: 4 },
    { name: "Liz Linder", precinct: 4 },
    { name: "Lisa Sears", precinct: 4 },
    { name: "Wendy Friedman", precinct: 5 },
    { name: "Ariel Gruzman", precinct: 5 },
    { name: "Omar Mabrouk", precinct: 5 },
    { name: "Hugh Mattison", precinct: 5 },
    { name: "Beth Miller", precinct: 5 },
    { name: "John Bassett", precinct: 6 },
    { name: "Arthur Conquest, III", precinct: 6 },
    { name: "Patricia Correa", precinct: 6 },
    { name: "Scott Englander", precinct: 6 },
    { name: "Daniel Fishman", precinct: 6 },
    { name: "Ruvi Kitov", precinct: 6 },
    { name: "Bitsy Perlman", precinct: 6 },
    { name: "Dave Porter", precinct: 6 },
    { name: "Jeff Rudolph", precinct: 6 },
    { name: "Kim Smith", precinct: 6 },
    { name: "Virginia Smith", precinct: 6 },
    { name: "Marissa Vogt", precinct: 6 },
    { name: "Kelly Hardebeck", precinct: 7 },
    { name: "John Hebert", precinct: 7 },
    { name: "Emily Lipman", precinct: 7 },
    { name: "Jonathan Margolis", precinct: 7 },
    { name: "Samantha Rothberg", precinct: 7 },
    { name: "Chi Chi Wu", precinct: 7 },
    { name: "Kailey Bennett", precinct: 8 },
    { name: "Tracie Burns", precinct: 8 },
    { name: "John Harris", precinct: 8 },
    { name: "William Heinemann", precinct: 8 },
    { name: "Nancy Heller", precinct: 8 },
    { name: "Charles Homer", precinct: 8 },
    { name: "Anita Johnson", precinct: 8 },
    { name: "Mia Sievers", precinct: 8 },
    { name: "Michael Toffel", precinct: 8 },
    { name: "David Tytell", precinct: 8 },
    { name: "Lee Biernbaum", precinct: 9 },
    { name: "Matthew Caldeira", precinct: 9 },
    { name: "Christopher Mutty", precinct: 9 },
    { name: "Gary Shiffman", precinct: 9 },
    { name: "Harold Simansky", precinct: 9 },
    { name: "Gerald Tuckman", precinct: 9 },
    { name: "Robert Weintraub", precinct: 9 },
    { name: "Katherine Wilkins", precinct: 9 },
    { name: "Suzette Abbott", precinct: 10 },
    { name: "John Bowman", precinct: 10 },
    { name: "Elizabeth Erdman", precinct: 10 },
    { name: "Katherine Florio", precinct: 10 },
    { name: "Elizabeth Kernan", precinct: 10 },
    { name: "David Klafter", precinct: 10 },
    { name: "Jessica Lang", precinct: 10 },
    { name: "Kimberly Loscalzo", precinct: 10 },
    { name: "Ariel Maddocks", precinct: 10 },
    { name: "Nimisha Schneider", precinct: 10 },
    { name: "Naomi Sweitzer", precinct: 10 },
    { name: "Carol Caro", precinct: 11 },
    { name: "Shira Fischer", precinct: 11 },
    { name: "Benjamin Garfinkel", precinct: 11 },
    { name: "Shanna Giora-Gorfajn", precinct: 11 },
    { name: "Jennifer Goldsmith", precinct: 11 },
    { name: "Andrew Halpert", precinct: 11 },
    { name: "John Hilliard", precinct: 11 },
    { name: "David Lescohier", precinct: 11 },
    { name: "Leonard Wholey", precinct: 11 },
    { name: "Petra Bignami", precinct: 12 },
    { name: "Michael Burstein", precinct: 12 },
    { name: "Emily Dolbear", precinct: 12 },
    { name: "Harry Friedman", precinct: 12 },
    { name: "Amy Hummel", precinct: 12 },
    { name: "Jonathan Karon", precinct: 12 },
    { name: "Eric McNulty", precinct: 12 },
    { name: "Margaret Molloy", precinct: 12 },
    { name: "Jonathan Phillips", precinct: 12 },
    { name: "Jeremy Redburn", precinct: 12 },
    { name: "Michael Best", precinct: 13 },
    { name: "Leigha Cuniberti", precinct: 13 },
    { name: "Victoria Fernandez", precinct: 13 },
    { name: "Jonathan Fine", precinct: 13 },
    { name: "Anne Finkenbinder", precinct: 13 },
    { name: "Andrew Fischer", precinct: 13 },
    { name: "Brian Ladd", precinct: 13 },
    { name: "Werner Lohe", precinct: 13 },
    { name: "David Mendels", precinct: 13 },
    { name: "Tommy Renkes", precinct: 13 },
    { name: "Paul Saner", precinct: 13 },
    { name: "Jennifer Segel", precinct: 13 },
    { name: "Philip Steigman", precinct: 13 },
    { name: "Carla Benka", precinct: 14 },
    { name: "David Eisenberg", precinct: 14 },
    { name: "Brenda Hefter", precinct: 14 },
    { name: "Jesse Hefter", precinct: 14 },
    { name: "Pamela Lodish", precinct: 14 },
    { name: "Carlos Ridruejo", precinct: 14 },
    { name: "Lynda Roseman", precinct: 14 },
    { name: "Sharon Schoffmann", precinct: 14 },
    { name: "Eileen Berger", precinct: 15 },
    { name: "Michael Berger", precinct: 15 },
    { name: "Arisa Boit", precinct: 15 },
    { name: "Jane Flanagan", precinct: 15 },
    { name: "Benedicte Hallowell", precinct: 15 },
    { name: "Rory Hallowell", precinct: 15 },
    { name: "Deborah Hatzieleftheriadis", precinct: 15 },
    { name: "Hugh Joseph", precinct: 15 },
    { name: "Janice Kahn", precinct: 15 },
    { name: "Kristine Knauf", precinct: 15 },
    { name: "Ira Krepchin", precinct: 15 },
    { name: "Richard Nangle", precinct: 15 },
    { name: "Wadner Oge", precinct: 15 },
    { name: "James Rourke", precinct: 15 },
    { name: "Jean Senat-Fleury", precinct: 15 },
    { name: "Deborah Abner", precinct: 16 },
    { name: "Laura Baines-Walsh", precinct: 16 },
    { name: "Adrienne Bowman", precinct: 16 },
    { name: "Stephen Chiumenti", precinct: 16 },
    { name: "David Day", precinct: 16 },
    { name: "Nick Fetzer", precinct: 16 },
    { name: "Regina Frawley", precinct: 16 },
    { name: "Scott Gladstone", precinct: 16 },
    { name: "Michael Harrington", precinct: 16 },
    { name: "Julie Jette", precinct: 16 },
    { name: "Alisa Jonas", precinct: 16 },
    { name: "Judith Leichtner", precinct: 16 },
    { name: "Carolyn Thall", precinct: 16 },
    { name: "Frank Cohen", precinct: 17 },
    { name: "Jonathan Davis", precinct: 17 },
    { name: "Benjamin Dubin", precinct: 17 },
    { name: "Jonathan Golden", precinct: 17 },
    { name: "Katherine Haenschen", precinct: 17 },
    { name: "Jonathan Klein", precinct: 17 },
    { name: "Bruce Levin", precinct: 17 },
    { name: "Susan Park", precinct: 17 },
    { name: "Linda Pehlke", precinct: 17 },
    { name: "Elizabeth Schafer", precinct: 17 },
    { name: "Joseph Valencia", precinct: 17 },
  ],
};
